import React, { useState, useEffect } from "react";
import { uploadFileToS3Complete } from "../../api/s3";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { RefreshProjectProps } from "../../types/screensTypes";
import { useGlobalState } from "../../context/useGlobalState";
import { addItemsToProject } from "../../api";
import Dropdown from "../../components/common/Dropdown";
import TextInput from "../../components/ui/TextInput";
import { FileInput } from "../../components/common/FileInput";
import TitleSubtitle from "../../components/common/TitleSubtitle";
import Button from "../../components/ui/Button";
import { ProjectItemUpload } from "../../types/requestInputs";
import { AUDIO_FILE_ACCEPT, isAcceptedAudioFile } from "../../utils/fileValidation";
import styles from "./AddItem.module.css";

interface UploadedAudio {
    name: string;
    link: string;
}

interface FieldErrors {
    selectedContentLanguage: boolean;
    content: boolean;
    title: boolean;
}

const AddItemToProject: React.FC<RefreshProjectProps> = ({ refreshProjects }) => {
    const { t: rawT } = useTranslation();
    const t = rawT as any;
    const { data, loading, error } = useGlobalState();

    const [selectedContentLanguage, setSelectedContentLanguage] = useState<string | null>(null);
    const [title, setTitle] = useState<string>("");
    const [text, setText] = useState<string>("");
    const [audios, setAudios] = useState<UploadedAudio[]>([]);
    // An uploaded recording has no voice row of ours. Naming it here means the
    // translation screen offers it as its own mapping entry rather than lumping every
    // such audio under "unknown voice".
    const [voiceLabel, setVoiceLabel] = useState<string>("");
    const [uploadsInFlight, setUploadsInFlight] = useState<number>(0);
    const [audioTypeError, setAudioTypeError] = useState<boolean>(false);
    const [showErrors, setShowErrors] = useState<boolean>(false);
    const [fieldErrors, setFieldErrors] = useState<FieldErrors>({
        selectedContentLanguage: false,
        content: false,
        title: false,
    });
    const [isGenerateButtonDisabled, setIsGenerateButtonDisabled] = useState<boolean>(true);

    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    // Several audios describe several capsules, so a single title and text can't belong
    // to any of them: those files are imported in bulk and named after themselves.
    const isBulkImport = audios.length > 1;
    const hasTextWithSeveralAudios = isBulkImport && text.trim().length > 0;
    // A capsule is made of a text, an audio, or both. With no audio there is no file
    // name to fall back on, so the title becomes mandatory.
    const hasNoContent = !text.trim() && audios.length === 0;
    const isTitleMissing = audios.length === 0 && !title.trim();

    const handleMultipleFilesSelected = (files: File | File[]) => {
        const fileArray = Array.isArray(files) ? files : [files];
        const accepted = fileArray.filter(isAcceptedAudioFile);
        if (accepted.length < fileArray.length) {
            setAudioTypeError(true);
        } else {
            setAudioTypeError(false);
        }
        if (accepted.length === 0) return;

        const token = localStorage.getItem("token");
        if (!token) {
            console.error("No authentication token found");
            return;
        }

        setUploadsInFlight((prev) => prev + accepted.length);

        accepted.forEach((file) => {
            uploadFileToS3Complete(
                {
                    file,
                    isPublic: true,
                    isFolder: false,
                },
                token,
            )
                .then((result: string) => {
                    setAudios((prev) => [
                        ...prev,
                        {
                            name: file.name.replace(/\.[^/.]+$/, ""),
                            link: result,
                        },
                    ]);
                })
                .catch((error) => {
                    console.log(error);
                })
                .finally(() => {
                    setUploadsInFlight((prev) => Math.max(prev - 1, 0));
                });
        });
    };

    const removeAudio = (index: number) => {
        setAudios((prev) => prev.filter((_, i) => i !== index));
    };

    useEffect(() => {
        const errors: FieldErrors = {
            selectedContentLanguage: !selectedContentLanguage,
            content: hasNoContent,
            title: isTitleMissing,
        };

        setFieldErrors(errors);
        setIsGenerateButtonDisabled(
            Object.values(errors).some((err) => err === true) || hasTextWithSeveralAudios || uploadsInFlight > 0,
        );
    }, [selectedContentLanguage, hasNoContent, isTitleMissing, hasTextWithSeveralAudios, uploadsInFlight]);

    const showErrorMessage = () => {
        setShowErrors(true);
        setTimeout(() => {
            setShowErrors(false);
        }, 3000);
    };

    const buildItems = (): ProjectItemUpload[] => {
        if (isBulkImport) {
            return audios.map((audio) => ({ name: audio.name, link: audio.link }));
        }
        return [
            {
                name: title.trim() || audios[0]?.name || "",
                text: text.trim() || undefined,
                link: audios[0]?.link,
            },
        ];
    };

    const saveItem = async () => {
        if (!selectedContentLanguage || hasNoContent || isTitleMissing || hasTextWithSeveralAudios) {
            showErrorMessage();
            return;
        }

        setIsLoading(true);

        try {
            const token = localStorage.getItem("token");

            if (!token || !id) {
                setIsLoading(false);
                return;
            }

            const resp = await addItemsToProject({
                token,
                projectId: id,
                language: selectedContentLanguage,
                items: buildItems(),
                voiceLabel: audios.length > 0 ? voiceLabel.trim() || undefined : undefined,
            });

            if (resp && resp.message) {
                navigate(`/projects/${id}`);
            }
            setIsLoading(false);
        } catch (error) {
            console.error("Failed to save item:", error);
            setIsLoading(false);
        }
    };

    const submitLabel = uploadsInFlight > 0 ? t("uploading") : isLoading ? t("processing") : t("add");

    return (
        <div className={styles.page}>
            <div className={styles.stack}>
                <div className={styles.field}>
                    <TitleSubtitle
                        title={t("inWhichLanguageWouldYouLikeAddContent")}
                        subtitle={t("addItemContentHint")}
                        addMargin={false}
                    />
                    <Dropdown
                        label=""
                        options={data.languagesAI}
                        onChange={setSelectedContentLanguage}
                        loading={loading}
                        error={error ? true : false}
                        placeholder={t("language")}
                        value={selectedContentLanguage}
                        isMissing={showErrors && fieldErrors.selectedContentLanguage}
                        isWithoutMargin
                    />
                </div>

                {selectedContentLanguage && (
                    <>
                        <div className={styles.field}>
                            <TitleSubtitle
                                title={t("whatIsTheTitleOfYourStory")}
                                subtitle={t("addItemTitleSubtitle")}
                                addMargin={false}
                            />
                            <TextInput
                                placeholder={t("enterTitle")}
                                value={title}
                                onChange={(e) => setTitle(e.target.value)}
                                size="medium"
                                isMissing={showErrors && fieldErrors.title}
                                disabled={isBulkImport}
                            />
                        </div>

                        <div className={styles.field}>
                            <TitleSubtitle
                                title={t("textEdition")}
                                subtitle={t("addItemTextSubtitle")}
                                addMargin={false}
                            />
                            <TextInput
                                placeholder={t("pleaseInsertYourText")}
                                value={text}
                                onChange={(e) => setText(e.target.value)}
                                size="big"
                                numberOfLines={12}
                                isMissing={showErrors && fieldErrors.content}
                            />
                            {hasTextWithSeveralAudios && (
                                <p className={styles.hintWarning}>{t("addItemTextNeedsSingleAudio")}</p>
                            )}
                        </div>

                        <div className={styles.field}>
                            <TitleSubtitle
                                title={t("audioUpload")}
                                subtitle={t("audioFormatHelptext")}
                                addMargin={false}
                            />
                            <FileInput
                                multiple
                                onFileSelected={handleMultipleFilesSelected}
                                placeholder={t("dragAndDropOrClickMultiple")}
                                isMissing={showErrors && fieldErrors.content}
                                acceptedFileTypes={AUDIO_FILE_ACCEPT}
                            />
                            {audioTypeError && <p className={styles.hintWarning}>{t("unsupportedFileType")}</p>}
                            {uploadsInFlight > 0 && <p className={styles.hint}>{t("uploading")}…</p>}
                            {audios.length > 0 && (
                                <div className={styles.fileList}>
                                    <strong>{t("uploadedFiles")}:</strong>
                                    <ul className={styles.fileItems}>
                                        {audios.map((audio, index) => (
                                            <li key={audio.link} className={styles.fileItem}>
                                                <a
                                                    className={styles.fileName}
                                                    href={audio.link}
                                                    target="_blank"
                                                    rel="noreferrer"
                                                >
                                                    {audio.name}
                                                </a>
                                                <button
                                                    type="button"
                                                    className={styles.fileRemove}
                                                    aria-label={t("removeFile")}
                                                    onClick={() => removeAudio(index)}
                                                >
                                                    {t("remove")}
                                                </button>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                            )}
                            {isBulkImport && <p className={styles.hint}>{t("addItemNamesFromFiles")}</p>}
                        </div>

                        {audios.length > 0 && (
                            <div className={styles.field}>
                                <TitleSubtitle
                                    title={t("uploadVoiceNameTitle")}
                                    subtitle={t("uploadVoiceNameSubtitle")}
                                    addMargin={false}
                                />
                                <TextInput
                                    placeholder={t("transcriptVoiceCustomPlaceholder")}
                                    value={voiceLabel}
                                    onChange={(e) => setVoiceLabel(e.target.value)}
                                    fullWidth
                                    size="small"
                                />
                            </div>
                        )}

                        <div className={styles.actions}>
                            <Button
                                label={submitLabel}
                                onClick={saveItem}
                                variant="primary"
                                disabled={isGenerateButtonDisabled}
                                showError={showErrorMessage}
                            />
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default AddItemToProject;
